import React, { useMemo, useState, useContext, useEffect } from 'react';
import { UserContext } from './App';
import { authFetch, fetchCsrfToken } from './authFetch';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function asString(val) {
  if (val == null) return '';
  if (typeof val === 'string') return val;
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
}

// Progress Bar Component
function ImportProgressBar({ batchId, onComplete, onError }) {
  const [progress, setProgress] = useState({
    status: 'starting',
    processed_rows: 0,
    total_rows: 0,
    created_count: 0,
    updated_count: 0,
    skipped_count: 0,
    error_count: 0,
    current_step: 'Initializing...',
    progress_percentage: 0
  });

  useEffect(() => {
    if (!batchId) return;

    const pollProgress = async () => {
      try {
        const response = await authFetch(`${BASE_URL}/api/imports/progress/${batchId}/`);
        
        if (response.ok) {
          const data = await response.json();
          setProgress(data);
          
          if (data.status === 'completed') {
            onComplete(data);
            return; // Stop polling
          } else if (data.status === 'error') {
            onError(data.error_message || 'Import failed');
            return; // Stop polling
          }
        } else {
          console.error('Failed to fetch progress:', response.status);
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    };

    // Initial poll
    pollProgress();
    
    // Poll every 2 seconds while processing
    const interval = setInterval(() => {
      if (progress.status === 'completed' || progress.status === 'error') {
        clearInterval(interval);
        return;
      }
      pollProgress();
    }, 2000);

    return () => clearInterval(interval);
  }, [batchId, onComplete, onError]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      case 'processing': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✅';
      case 'error': return '❌';
      case 'processing': return '⏳';
      default: return '🔄';
    }
  };

  return (
    <div className="bg-white border rounded-lg p-6 shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Import Progress</h3>
        <span className="text-sm text-gray-500">Batch ID: {batchId}</span>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>{progress.current_step}</span>
          <span>{progress.progress_percentage || 0}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-all duration-300 ${getStatusColor(progress.status)}`}
            style={{ width: `${progress.progress_percentage || 0}%` }}
          ></div>
        </div>
      </div>

      {/* Status Information */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div className="bg-blue-50 p-3 rounded">
          <div className="font-semibold text-blue-800">Processed</div>
          <div className="text-xl text-blue-600">
            {progress.processed_rows} / {progress.total_rows}
          </div>
        </div>
        <div className="bg-green-50 p-3 rounded">
          <div className="font-semibold text-green-800">Created</div>
          <div className="text-xl text-green-600">{progress.created_count}</div>
        </div>
        <div className="bg-yellow-50 p-3 rounded">
          <div className="font-semibold text-yellow-800">Updated</div>
          <div className="text-xl text-yellow-600">{progress.updated_count}</div>
        </div>
        <div className="bg-gray-50 p-3 rounded">
          <div className="font-semibold text-gray-800">Skipped</div>
          <div className="text-xl text-gray-600">{progress.skipped_count}</div>
        </div>
      </div>

      {progress.error_count > 0 && (
        <div className="mt-4 bg-red-50 p-3 rounded border border-red-200">
          <div className="font-semibold text-red-800">Errors: {progress.error_count}</div>
          <div className="text-sm text-red-600">Some rows failed to process</div>
        </div>
      )}

      {/* Status indicator */}
      <div className="mt-4 flex items-center justify-center">
        <span className="text-2xl mr-2">{getStatusIcon(progress.status)}</span>
        <span className="font-medium capitalize">
          {progress.status === 'processing' ? 'Processing...' : progress.status}
        </span>
      </div>
    </div>
  );
}

export default function AdminImports() {
  const { currentUser } = useContext(UserContext) || { currentUser: null };
  const [file, setFile] = useState(null);
  const [importType, setImportType] = useState('U65_TRACKER');
  const [dryRun, setDryRun] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  // New state for progress tracking
  const [importProgress, setImportProgress] = useState(null);
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [forceReimport, setForceReimport] = useState(false);

  const isAdmin =
    !!(currentUser?.is_staff) ||
    (currentUser?.groups || []).some((g) => (g?.name || '').toLowerCase() === 'admin');

  const disabled = useMemo(() => !isAdmin || !file || processing, [isAdmin, file, processing]);

  if (!isAdmin) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold">Imports</h1>
        <p className="mt-2 text-red-600">You don't have permission to import. Admin role required.</p>
      </div>
    );
  }

  async function onDryRun() {
    if (!file) return;
    setError(''); setMessage(''); setImportProgress(null);
    setProcessing(true);
    try {
      // CRITICAL FIX: Ensure we have a fresh CSRF token before POST request
      await fetchCsrfToken();
      
      const form = new FormData();
      form.append('file', file);
      form.append('import_type', importType);

      const res = await authFetch(`${BASE_URL}/api/imports/dry-run/`, {
        method: 'POST',
        body: form,
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }

      const data = await res.json();
      console.log('[DryRun] response:', data);

      setDryRun(data);
      setMessage('Dry-run complete. Review the preview below.');
    } catch (e) {
      console.error('[DryRun] error:', e);
      setError(asString(e?.message || e));
    } finally {
      setProcessing(false);
    }
  }

  // Updated function to use async import with progress
  async function onCommitAndProcess() {
    if (!file) return;
    setError(''); setMessage(''); setImportProgress(null);
    setProcessing(true);
    
    try {
      // CRITICAL FIX: Ensure we have a fresh CSRF token before POST request
      await fetchCsrfToken();
      
      const form = new FormData();
      form.append('file', file);
      form.append('import_type', importType);
      if (forceReimport) {
        form.append('force_reimport', 'true');
      }

      const response = await authFetch(`${BASE_URL}/api/imports/commit-and-process-async/`, {
        method: 'POST',
        body: form,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Processing failed (${response.status})`);
      }

      const result = await response.json();
      console.log('[CommitAndProcessAsync] response:', result);

      // Start progress tracking
      setCurrentBatchId(result.batch_id);
      setImportProgress({
        status: 'processing',
        total_rows: result.total_rows,
        batch_id: result.batch_id
      });
      setMessage(`Import started successfully! Tracking progress for ${result.total_rows} rows...`);
      
    } catch (e) {
      console.error('[CommitAndProcessAsync] error:', e);
      setError(asString(e?.message || e));
      setProcessing(false);
    }
  }

  const handleImportComplete = (finalProgress) => {
    setProcessing(false);
    setMessage(`Import completed successfully!\n` +
      `Created: ${finalProgress.created_count || 0} customers\n` +
      `Updated: ${finalProgress.updated_count || 0} customers\n` +
      `Skipped: ${finalProgress.skipped_count || 0} rows\n` +
      `Errors: ${finalProgress.error_count || 0} rows\n` +
      `Batch ID: ${finalProgress.batch_id}`);

    // Clear the form
    setFile(null);
    setDryRun(null);
    setImportProgress(null);
    setCurrentBatchId(null);
  };

  const handleImportError = (errorMessage) => {
    setProcessing(false);
    setError(`Import failed: ${errorMessage}`);
    setImportProgress(null);
    setCurrentBatchId(null);
  };

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="text-2xl font-semibold mb-4">Admin: Florida Blue Imports</h1>
      
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
        <h3 className="font-semibold text-blue-800">Import Guidelines:</h3>
        <ul className="text-sm text-blue-700 mt-2 space-y-1">
          <li>• Customer info (name, address, phone, email) will be updated with latest data</li>
          <li>• Policy history is preserved - all applications are tracked</li>
          <li>• FB_UID is the permanent customer identifier once assigned</li>
          <li>• Contract ID links applications before FB_UID is assigned</li>
          <li>• All CSV columns are imported for complete audit trail</li>
        </ul>
      </div>

      {/* Progress Bar - Show when import is in progress */}
      {importProgress && (
        <div className="mb-6">
          <ImportProgressBar 
            batchId={currentBatchId}
            onComplete={handleImportComplete}
            onError={handleImportError}
          />
        </div>
      )}

      <div className="border rounded-lg p-4 space-y-4 bg-white shadow">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Import Type</label>
            <select
              className="w-full border rounded px-3 py-2"
              value={importType}
              onChange={(e) => setImportType(e.target.value)}
              disabled={processing}
            >
              <option value="U65_TRACKER">Under 65 Application Tracker</option>
              <option value="O65_TRACKER">Over 65 Application Tracker</option>
              <option value="U65_ALIGNED">Under 65 Aligned Accounts</option>
              <option value="O65_ALIGNED">Over 65 Aligned Accounts</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">CSV File</label>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="w-full border rounded px-3 py-2"
              disabled={processing}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="forceReimport"
            checked={forceReimport}
            onChange={(e) => setForceReimport(e.target.checked)}
            className="w-4 h-4"
            disabled={processing}
          />
          <label htmlFor="forceReimport" className="text-sm text-gray-700">
            Force re-import (allow previously imported files)
          </label>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onDryRun}
            disabled={disabled}
            className={`px-4 py-2 rounded text-white ${disabled ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {processing && !importProgress ? 'Analyzing...' : 'Dry Run Preview'}
          </button>

          <button
            onClick={onCommitAndProcess}
            disabled={disabled || !dryRun}
            className={`px-4 py-2 rounded text-white ${(disabled || !dryRun) ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'}`}
            title={!dryRun ? 'Run a dry-run first' : 'Process the import'}
          >
            {processing && importProgress ? 'Processing...' : 'Import File'}
          </button>

          {/* Cancel button when import is in progress */}
          {importProgress && (
            <button
              onClick={() => {
                setProcessing(false);
                setImportProgress(null);
                setCurrentBatchId(null);
                setMessage('Import monitoring stopped (import continues in background)');
              }}
              className="px-4 py-2 rounded text-white bg-red-600 hover:bg-red-700"
            >
              Stop Monitoring
            </button>
          )}
        </div>

        {!importProgress && message && <div className="text-sm text-green-700 whitespace-pre-wrap bg-green-50 p-3 rounded border">{asString(message)}</div>}
        {error && <div className="text-sm text-red-600 whitespace-pre-wrap bg-red-50 p-3 rounded border">{asString(error)}</div>}
      </div>

      {/* Only show dry run results when not importing */}
      {dryRun && !importProgress && (
        <div className="mt-6 space-y-4">
          {/* Summary Stats */}
          <div className="border rounded-lg p-4 bg-white shadow">
            <h2 className="text-lg font-semibold mb-3">Import Preview Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-blue-50 p-3 rounded">
                <div className="font-semibold text-blue-800">Total Rows</div>
                <div className="text-xl text-blue-600">{dryRun.total_rows}</div>
              </div>
              <div className="bg-green-50 p-3 rounded">
                <div className="font-semibold text-green-800">Will Create</div>
                <div className="text-xl text-green-600">{dryRun.would_create_customers} customers</div>
              </div>
              <div className="bg-yellow-50 p-3 rounded">
                <div className="font-semibold text-yellow-800">Will Update</div>
                <div className="text-xl text-yellow-600">{dryRun.would_update_customers} customers</div>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <div className="font-semibold text-gray-800">Will Skip</div>
                <div className="text-xl text-gray-600">{dryRun.rows_missing_keys} rows</div>
              </div>
            </div>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <strong>Unique Customers:</strong> {dryRun.unique_customers}
              </div>
              <div>
                <strong>Policy Records to Add:</strong> {dryRun.policy_records_to_add}
              </div>
              <div className={`font-semibold ${dryRun.duplicate_file ? 'text-red-600' : 'text-green-600'}`}>
                <strong>Duplicate File:</strong> {dryRun.duplicate_file ? 'Yes - Already processed!' : 'No'}
              </div>
            </div>
          </div>

          {/* Key Columns Check */}
          <div className="border rounded-lg p-4 bg-white shadow">
            <h3 className="text-lg font-semibold mb-3">Column Validation</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
              {Object.entries(dryRun.key_columns_found || {}).map(([column, found]) => (
                <div key={column} className={`flex items-center space-x-2 ${found ? 'text-green-700' : 'text-red-700'}`}>
                  <span>{found ? '✓' : '✗'}</span>
                  <span>{column}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sample Data */}
          {dryRun.sample_data && dryRun.sample_data.length > 0 && (
            <div className="border rounded-lg p-4 bg-white shadow">
              <h3 className="text-lg font-semibold mb-3">Sample Data Preview</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Row</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Contract ID</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">FB_UID</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Applicant Name</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-500">Product</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {dryRun.sample_data.map((sample, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2">{sample.row}</td>
                        <td className="px-3 py-2 font-mono text-xs">{sample.contract_id || 'N/A'}</td>
                        <td className="px-3 py-2 font-mono text-xs">{sample.fb_uid || 'N/A'}</td>
                        <td className="px-3 py-2">{sample.applicant_name || 'N/A'}</td>
                        <td className="px-3 py-2">{sample.status || 'N/A'}</td>
                        <td className="px-3 py-2">{sample.product || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* All Columns Detected */}
          <div className="border rounded-lg p-4 bg-white shadow">
            <h3 className="text-lg font-semibold mb-3">All Columns Detected ({(dryRun.columns_detected || []).length})</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-xs">
              {(dryRun.columns_detected || []).map((column, index) => (
                <div key={index} className="bg-gray-50 p-2 rounded border">
                  {column}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}